import { useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { context, useSelectors } from '../../../../store'
import { settings } from '../../../../store/constants'
import { saveRawBookingState } from '../../../../store/booking-persistence'
import { formDraft } from '../../../../lib/booking-draft'
import { capturePayload, captureKey, draftToken, restoreToSaved, resumeTokenFrom, validEmail, validMobile } from '../../../../lib/booking-capture'
import { AnalyticsService } from '../../../../services/analytics'

// Unfinished bookings (lib/booking-capture.js; BookingDraft). Two jobs:
//
// 1. Save. Whenever the customer leaves a box or a choice on Book & pay
//    (focusout anywhere in the form, so the card path's email box counts
//    without touching it), and once a valid email or UK mobile is there, the
//    booking as it stands is posted, at most once per change. Leaving the
//    page posts the last change by beacon.
// 2. Resume. /booking?resume=<token> (the reminder's link): the booking is
//    fetched and written where Book & pay keeps its draft, BEFORE the form
//    restores (`pending` holds useBookDraft back), then the token leaves the
//    address bar.
//
// Only a person counts. Link-preview scanners (Meta/WhatsApp, Slack,
// iMessage; 21 Oct 2026, Meta datacentres, viewport 1050) open the link and
// run this page. Fetching the booking changes nothing on the server, and
// nothing is recorded or saved until the first real tap, key or typed value
// (`touched`): only then does `booking_resumed` fire and the draft get its
// resumed_at (POST …/resumed), and only then may leaving the page post the
// booking (a restored mobile must not make a new draft, and a new reminder,
// for a page nobody used).
//
// The answer is kept for the page, not the component: Book & pay remounts
// when the address bar changes, and a remount must neither ask again nor
// lose the answer the first touch reports. (Until 22 Oct 2026 the token
// stayed in the address bar, services/history.js keeps the query, so every
// answer remounted the form and asked again: a loop of GETs and, before the
// touch rule, of `booking_resumed`.)
//
// Never blocks or changes the booking itself: every failure is silent.
let resumed = null

const posthog = () => (typeof window !== 'undefined' ? window.posthog : null)
const phCall = (name, ...args) => {
  try {
    const ph = posthog()
    return ph && typeof ph[name] === 'function' ? ph[name](...args) : null
  } catch (e) { return null }
}

const postJson = (path, body) => fetch(`${settings.host}/api/v2/booking_drafts${path}`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  body: JSON.stringify(body)
})

const sessionStore = () => {
  try { return window.sessionStorage } catch (e) { return null }
}

// The email and a first name, from the boxes on screen: the card path's
// email (#book-email, one-checkout) and any given-name box; and the WhatsApp
// opt-in box (step-checkout, data-whatsapp-optin), undefined when not shown.
const typedInPage = form => {
  if (typeof document === 'undefined' || !form) return {}
  const email = form.querySelector('input[type="email"]')
  const first = form.querySelector('input[autocomplete="given-name"]')
  const optin = form.querySelector('input[data-whatsapp-optin]')
  return { email: email ? email.value : '', firstName: first ? first.value : '', whatsappOptin: optin ? !!optin.checked : undefined }
}

export const useDraftCapture = ({ bookingForm, formId = 'booking-step-form' }) => {
  const history = useHistory()
  const { state } = useContext(context)
  const { prices, services, baseService, price } = useSelectors()
  const resumeToken = resumeTokenFrom(history.location.search)
  const [pending, setPending] = useState(!!resumeToken && !resumed)
  const touched = useRef(false)

  // Once, when there are both the resume answer ({ token, restored, status })
  // and a first touch.
  const reportResumed = () => {
    const r = resumed
    if (!r || !touched.current || r.sent) return
    r.sent = 1
    AnalyticsService.trackBookingResumed(r)
    if (r.restored) postJson('/resumed', { token: r.token }).catch(() => {})
  }

  // --- Resume -------------------------------------------------------------
  useEffect(() => {
    if (!resumeToken || resumed) return
    let done = false
    const finish = (restored, status) => {
      if (done) return
      done = true
      resumed = { token: resumeToken, restored, status }
      reportResumed()
      // The token out of the address bar, the rest of the query kept.
      try {
        const params = new URLSearchParams(history.location.search)
        params.delete('resume')
        const rest = params.toString()
        history.replace({ pathname: history.location.pathname, search: rest ? `?${rest}` : '', replaceQuery: true })
      } catch (e) { /* keep the page */ }
      setPending(false)
    }
    fetch(`${settings.host}/api/v2/booking_drafts/resume?token=${encodeURIComponent(resumeToken)}`, { headers: { Accept: 'application/json' } })
      .then(res => (res.ok ? res.json() : null))
      .then(json => {
        const saved = json && json.success ? restoreToSaved(json.restore) : null
        if (saved) saveRawBookingState(saved)
        finish(!!saved, json && json.status)
      })
      .catch(() => finish(false))
    // Never stand in the way of the form for long.
    const timer = setTimeout(() => finish(false), 6000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- Save ---------------------------------------------------------------
  // What the payload is built from, current at every event.
  const latest = useRef({})
  latest.current = { state, prices, services, baseService, price }
  const sentKey = useRef('')
  const sentKinds = useRef({ email: false, mobile: false, any: false })

  const buildBody = () => {
    const { state: s, prices: p, services: sel, baseService: base, price: total } = latest.current
    const form = typeof document !== 'undefined' ? document.getElementById(formId) : null
    const values = bookingForm.getValues()
    const typed = typedInPage(form)
    const chosen = Object.entries(sel || {}).filter(([, on]) => on).map(([id]) => p.byId[id]).filter(Boolean)
    const hasWash = chosen.some(service => service.group === 'base')
    return capturePayload({
      token: draftToken(sessionStore()),
      email: typed.email,
      mobile: values.mobile,
      firstName: typed.firstName,
      form: values,
      wash: hasWash ? base?.name : null,
      extras: chosen.filter(service => service.group !== 'base').map(service => service.name),
      total: total && typeof total.value === 'number' ? total.value : null,
      sessionId: phCall('get_session_id'),
      distinctId: phCall('get_distinct_id'),
      internal: phCall('get_property', 'internal') === true,
      whatsappOptin: typed.whatsappOptin,
      restore: { form: formDraft(values), services: s.services, servicesAvailability: s.servicesAvailability }
    })
  }

  const send = body => {
    const key = captureKey(body)
    if (!body || key === sentKey.current) return
    sentKey.current = key
    postJson('', body)
      .then(res => (res.ok ? res.json() : null))
      .then(json => {
        if (!json || !json.success) return
        const hasEmail = !!body.email
        const hasMobile = !!body.mobile
        const kinds = sentKinds.current
        if (json.created || (hasEmail && !kinds.email) || (hasMobile && !kinds.mobile)) {
          AnalyticsService.trackBookingDraftSaved({ first: !!json.created || !kinds.any, hasEmail, hasMobile })
        }
        sentKinds.current = { email: kinds.email || hasEmail, mobile: kinds.mobile || hasMobile, any: true }
      })
      .catch(() => { sentKey.current = '' })
  }

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    let timer = null
    const onLeaveField = () => {
      if (!touched.current) return
      clearTimeout(timer)
      // After the field's own blur work (the mobile is tidied on blur).
      timer = setTimeout(() => {
        try {
          send(buildBody())
        } catch (e) { /* never in the customer's way */ }
      }, 300)
    }
    // A typed email or mobile with no blur yet (they closed the tab).
    const onPageHide = () => {
      if (!touched.current) return
      try {
        const values = bookingForm.getValues()
        const form = document.getElementById(formId)
        if (!validMobile(values.mobile) && !validEmail(typedInPage(form).email)) return
        const body = buildBody()
        const key = captureKey(body)
        if (!body || key === sentKey.current || !navigator.sendBeacon) return
        sentKey.current = key
        navigator.sendBeacon(`${settings.host}/api/v2/booking_drafts`, new Blob([JSON.stringify(body)], { type: 'application/json' }))
      } catch (e) { /* unloading */ }
    }
    // A ticked box need not take focus (Safari), so a change saves too.
    const onChange = ev => {
      if (ev.target && ev.target.matches && ev.target.matches('input[data-whatsapp-optin]')) onLeaveField()
    }
    // A person: the browser's own tap, key or typed value (isTrusted, never
    // a script's; `input` covers autofill without a key).
    const onTouch = ev => {
      if (ev.isTrusted && !touched.current) {
        touched.current = true
        reportResumed()
      }
    }
    const on = [['pointerdown', onTouch], ['keydown', onTouch], ['input', onTouch], ['focusout', onLeaveField], ['change', onChange]]
    on.forEach(([kind, fn]) => document.addEventListener(kind, fn, true))
    window.addEventListener('pagehide', onPageHide)
    return () => {
      clearTimeout(timer)
      on.forEach(([kind, fn]) => document.removeEventListener(kind, fn, true))
      window.removeEventListener('pagehide', onPageHide)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { pending }
}
